import { GetMeRequestConfig, useStorytellClient } from "@storytell/client";
import { subscribeToGlobalEvent } from "@storytell/events";
import type { MeResponse, ProjectId } from "@storytell/types";
import { createResource, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";

export type UserStore = ReturnType<typeof createUserStore>;

export const createUserStore = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
}) => {
  const client = useStorytellClient();
  // Preserve last-good so a transient failure doesn't blank a known-good user.
  // Tagged by userId so a same-tab re-auth can't leak the prior user's profile.
  let lastSuccessful: { userId: string; value: MeResponse } | undefined;

  const [meData, { refetch }] = createResource(
    () => deps.authReady() && deps.userId(),
    async (): Promise<MeResponse | undefined> => {
      const currentUserId = deps.userId();
      if (lastSuccessful && lastSuccessful.userId !== currentUserId) {
        lastSuccessful = undefined;
      }
      const request = GetMeRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      if (response.isOk()) {
        if (currentUserId) lastSuccessful = { userId: currentUserId, value: response.value };
        return response.value;
      }
      const cached = lastSuccessful;
      return cached && cached.userId === currentUserId ? cached.value : undefined;
    },
    { deferStream: true }
  );

  if (!isServer) {
    onMount(() => {
      const unsubscribeConnectivityRestored = subscribeToGlobalEvent("connectivity-restored", () => {
        void refetch();
      });
      const unsubscribeAuthRefresh = subscribeToGlobalEvent("auth-token-refreshed", () => {
        void refetch();
      });
      onCleanup(() => {
        unsubscribeConnectivityRestored();
        unsubscribeAuthRefresh();
      });
    });
  }

  const user = () => meData.latest;
  const defaultProjectId = (): ProjectId | undefined => meData.latest?.defaultProjectId;

  return {
    refetch,
    user,
    defaultProjectId,
  };
};
